import { createContext, useContext, useCallback, useRef, useState, useEffect, useId } from 'react';
import { AlertTriangle } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from '../components/ui/alert-dialog';

const UnsavedChangesContext = createContext(null);

const DEFAULT_MESSAGE = 'If you leave now, your unsaved changes will be lost.';

// App-wide tracker of "dirty" inline editors. Because the app uses BrowserRouter
// (not a data router), react-router's useBlocker is unavailable — so this gives
// automatic protection for full page reload / tab close (via the native
// beforeunload prompt, which browsers force) and a branded confirmDiscard()
// dialog that individual surfaces await in their own back/tab/close handlers.
export function UnsavedChangesProvider({ children }) {
  const registry = useRef(new Map());
  const [dirtyCount, setDirtyCount] = useState(0);

  const setDirty = useCallback((id, dirty) => {
    const had = registry.current.get(id) || false;
    if (dirty === had) return;
    if (dirty) registry.current.set(id, true);
    else registry.current.delete(id);
    setDirtyCount(registry.current.size);
  }, []);

  const hasUnsaved = dirtyCount > 0;

  // Branded confirm dialog state
  const [dialog, setDialog] = useState({ open: false, message: DEFAULT_MESSAGE });
  const resolverRef = useRef(null);

  const confirmDiscard = useCallback((message) => {
    if (registry.current.size === 0) return Promise.resolve(true);
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setDialog({ open: true, message: message || DEFAULT_MESSAGE });
    });
  }, []);

  const settle = useCallback((result) => {
    setDialog((d) => ({ ...d, open: false }));
    const resolve = resolverRef.current;
    resolverRef.current = null;
    if (resolve) resolve(result);
  }, []);

  // Global guard for full page reload / tab close (native prompt — unavoidable).
  useEffect(() => {
    if (!hasUnsaved) return undefined;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsaved]);

  return (
    <UnsavedChangesContext.Provider value={{ setDirty, hasUnsaved, confirmDiscard }}>
      {children}
      <AlertDialog open={dialog.open} onOpenChange={(open) => { if (!open) settle(false); }}>
        <AlertDialogContent data-testid="unsaved-changes-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-950/40 shrink-0">
                <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              </span>
              Discard unsaved changes?
            </AlertDialogTitle>
            <AlertDialogDescription>{dialog.message}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => settle(false)} data-testid="unsaved-keep-editing-btn">
              Keep editing
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => settle(true)}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              data-testid="unsaved-discard-btn"
            >
              Discard &amp; leave
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </UnsavedChangesContext.Provider>
  );
}

export function useUnsavedChanges() {
  return useContext(UnsavedChangesContext) || {
    setDirty: () => {},
    hasUnsaved: false,
    confirmDiscard: () => Promise.resolve(true),
  };
}

// Register a single editor's dirty state. Pass a boolean. Auto-deregisters on unmount.
export function useUnsavedGuard(dirty) {
  const { setDirty } = useUnsavedChanges();
  const id = useId();
  useEffect(() => {
    setDirty(id, !!dirty);
  }, [dirty, id, setDirty]);
  useEffect(() => () => setDirty(id, false), [id, setDirty]);
}

// Wrap a dialog's open state so closing (Esc/backdrop/X/Cancel) while dirty
// prompts via the branded confirmDiscard(). Returns a guarded onOpenChange +
// a requestClose() for explicit Cancel buttons. Also registers the dirty state
// so the native beforeunload reload/close prompt covers open dialogs too.
export function useDialogGuard({ open, dirty, setOpen, message }) {
  const { confirmDiscard } = useUnsavedChanges();
  useUnsavedGuard(open && dirty);
  const guardedOnOpenChange = useCallback(async (next) => {
    if (next) { setOpen(true); return; }
    if (dirty && !(await confirmDiscard(message))) return; // keep open
    setOpen(false);
  }, [dirty, setOpen, confirmDiscard, message]);
  const requestClose = useCallback(async () => {
    if (dirty && !(await confirmDiscard(message))) return;
    setOpen(false);
  }, [dirty, setOpen, confirmDiscard, message]);
  return { guardedOnOpenChange, requestClose };
}
